import React, { useRef, useState, useEffect } from "react";
import { cn } from "../../lib/utils";

interface OTPInputProps {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  error?: boolean;
}

export const OTPInput: React.FC<OTPInputProps> = ({
  length = 6,
  value,
  onChange,
  className,
  error = false,
}) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(""));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    const newOtp = value.split("").slice(0, length);
    setOtp([...newOtp, ...Array(length - newOtp.length).fill("")]);
  }, [value, length]);

  const handleChange = (index: number, digit: string) => {
    if (digit.length > 1) {
      // Handle paste
      const pastedData = digit.slice(0, length).split("");
      const newOtp = [...otp];
      pastedData.forEach((d, i) => {
        if (index + i < length) {
          newOtp[index + i] = d.replace(/\D/g, "");
        }
      });
      setOtp(newOtp);
      onChange(newOtp.join(""));

      // Focus last filled input or next empty
      const lastFilledIndex = Math.min(index + pastedData.length - 1, length - 1);
      inputRefs.current[lastFilledIndex]?.focus();
      return;
    }

    if (!/^\d*$/.test(digit)) return;

    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    onChange(newOtp.join(""));

    // Move to next input
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className={cn("flex gap-2", className)}>
      {Array(length)
        .fill(0)
        .map((_, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={otp[index]}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={(e) => {
              e.preventDefault();
              const pastedData = e.clipboardData.getData("text");
              handleChange(index, pastedData);
            }}
            className={cn(
              "w-7 h-9 sm:w-12 sm:h-14 text-center text-sm sm:text-lg font-semibold border rounded-none focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all",
              error ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-indigo-600",
              otp[index] && "border-indigo-600"
            )}
          />
        ))}
    </div>
  );
};
